import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { propColor } from "./TimeClock";
import { TechDayModal } from "../components/TechDayModal";
import { Users, Building2, MapPin, Coffee, MoonStar, RefreshCw, Clock } from "lucide-react";

const STATUS = {
  on_job: { label: "onJob", chip: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500", ring: "border-emerald-300" },
  standby: { label: "standbyStatus", chip: "bg-amber-100 text-amber-700", dot: "bg-amber-500", ring: "border-amber-200" },
  off: { label: "offShift", chip: "bg-slate-100 text-slate-500", dot: "bg-slate-300", ring: "border-slate-200" },
};

const timeSince = (iso, locale) => iso ? new Date(iso).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }) : "—";

export default function Crew() {
  const { t, lang } = useLang();
  const locale = lang === "en" ? "en-US" : "pt-BR";
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = () => {
    setRefreshing(true);
    api.get("/workforce/live").then(setData).catch(() => {}).finally(() => setRefreshing(false));
  };
  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, []); // eslint-disable-line

  const counts = data?.counts || { on_job: 0, standby: 0, off: 0 };
  const crew = data?.crew || [];

  const cards = [
    { key: "on_job", label: t("onJob"), value: counts.on_job, icon: Building2, cls: "text-emerald-600" },
    { key: "standby", label: t("standbyStatus"), value: counts.standby, icon: Coffee, cls: "text-amber-600" },
    { key: "off", label: t("offShift"), value: counts.off, icon: MoonStar, cls: "text-slate-400" },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2"><Users className="w-7 h-7 text-slate-400" />{t("crew")}</h1>
          <p className="text-slate-500 text-sm mt-1">{t("crewHint")}</p>
        </div>
        <button data-testid="crew-refresh" onClick={load} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 text-slate-600 text-sm font-medium px-3 py-2 hover:bg-slate-50">
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />{t("refresh")}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.key} data-testid={`crew-count-${c.key}`} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-400">{c.label}</span>
                <Icon className={`w-4 h-4 ${c.cls}`} />
              </div>
              <div className={`text-3xl font-bold mt-1 tabular-nums ${c.cls}`}>{c.value}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {crew.map((r) => {
          const st = STATUS[r.status];
          const pc = r.current_job ? propColor(r.current_job.unit_name) : null;
          return (
            <div key={r.tech_id} data-testid={`crew-tech-${r.tech_id}`} onClick={() => setSelected(r)}
              className={`bg-white border-2 rounded-2xl p-4 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${st.ring}`}>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <span className="w-11 h-11 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-base font-bold">{(r.tech_name || "?").slice(0, 1).toUpperCase()}</span>
                  <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white ${st.dot}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{r.tech_name}</div>
                  <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${st.chip}`}>{t(st.label)}</span>
                </div>
              </div>

              <div className="mt-3 min-h-[44px]">
                {r.current_job ? (
                  <div>
                    <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full ${pc.bg} ${pc.text}`}>
                      <Building2 className="w-3 h-3" />{r.current_job.unit_name || r.current_job.title}
                    </span>
                    <div className="text-xs text-slate-500 mt-1 truncate">{r.current_job.title}</div>
                  </div>
                ) : r.status === "standby" ? (
                  <div className="text-xs text-slate-500 flex items-center gap-1"><Coffee className="w-3.5 h-3.5" />{t("officeStandby")}</div>
                ) : (
                  <div className="text-xs text-slate-400">{t("notClockedIn")}</div>
                )}
              </div>

              <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between text-xs">
                <span className="text-slate-500 inline-flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  {r.status !== "off" ? `${t("since")} ${timeSince(r.job_since || r.shift_since, locale)}` : "—"}
                </span>
                <span className="font-bold text-slate-700 tabular-nums">{(r.job_hours_today || 0).toFixed(2)}h {t("today").toLowerCase()}</span>
                {r.location && <a href={`https://maps.google.com/?q=${r.location.lat},${r.location.lng}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-blue-500"><MapPin className="w-4 h-4" /></a>}
              </div>
            </div>
          );
        })}
      </div>
      {crew.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-400">
          <Users className="w-10 h-10 mx-auto mb-2 text-slate-300" />{t("noData")}
        </div>
      )}
      {selected && <TechDayModal tech={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
